import { formatDate } from '../formatting'
import { type SelectItem } from '../types'
import { toDisplayTimeline } from './bracket'
import { getRandomGameColor } from './color'
import {
  type BracketRound,
  DAY,
  type DecoratedGameParticipant,
  type GameData,
  type GameParticipantType,
  type GameSeedingType,
  type GameStatus,
  type GameType,
  getDefaultTerms,
  isNotDefined,
  pluralize,
  type UnsavedGame,
  type UserId,
} from '@terros/common'
import { DateTime } from 'luxon'

export type GameTypeDefinition = {
  type: GameType
  name: string
  description: string
  bestFor: string
}

export const COMPETITION_TYPE_DEFINITIONS: GameTypeDefinition[] = [
  {
    type: 'bracket',
    name: 'Tournament Bracket',
    description:
      'Single elimination tournament style bracket. Points are assigned to selected metric(s). Seeding round option.',
    bestFor: 'Ideal for longer competitions (4-8 weeks)',
  },
  {
    type: 'ladder',
    name: 'Leaderboard',
    description:
      'Teams or Sales Reps compete to stay atop the leaderboard. Points are assigned to selected metric(s). Three highest scores win the podium.',
    bestFor: 'Best for short- and medium-length competitions (1 day - 4 weeks)',
  },
  {
    type: 'raffle',
    name: 'Raffle',
    description:
      'Host a drawing to raffle off prizes to eligible teams or sales reps based on your qualification metrics.',
    bestFor: 'Great for short competitions (1-7 days)',
  },
]

export const toReadableCompetitionType = (gameType: GameType): string => {
  switch (gameType) {
    case 'ladder':
      return 'Leaderboard'
    case 'bracket':
      return 'Bracket'
    case 'raffle':
      return 'Raffle'
  }
}

export const COMPETITION_TYPE_SELECT_ITEMS: SelectItem<GameType>[] = COMPETITION_TYPE_DEFINITIONS.map((type) => ({
  value: type.type,
  label: type.name,
}))

export const DEFAULT_COMPETITION_NAME = 'Untitled Competition'

export const getNewDummyGame = (gameType: GameType): UnsavedGame => {
  const now = DateTime.now()
  const startDatetime = now.plus({ month: 1 }).startOf('week').set({ hour: 2 })
  const endDatetime = startDatetime.plus({ day: 5 })
  const startDate = startDatetime.toMillis()
  const announceDate = startDatetime.toMillis()
  const endDate = endDatetime.toMillis()
  const color = getRandomGameColor()
  const terms = getDefaultTerms(gameType)

  if (gameType === 'bracket') {
    return {
      name: DEFAULT_COMPETITION_NAME,
      status: 'draft',
      startDate,
      endDate,
      announceDate,
      color,
      gameType,
      awards: [],
      criteria: [],
      settings: {
        participantType: 'user',
      },
      rounds: [],
      tiebreakers: {
        startTime: startDate,
        metrics: [],
      },
      terms,
    }
  }
  return {
    name: DEFAULT_COMPETITION_NAME,
    status: 'draft',
    startDate,
    endDate,
    announceDate,
    color,
    gameType,
    awards: [],
    criteria: [],
    settings: {
      participantType: 'user',
    },
    tiebreakers: {
      startTime: startDate,
      metrics: [],
    },
    terms,
  }
}

export const getAddRemoveUserIds = (
  currentUsers: UserId[],
  newUsers: UserId[]
): { add: UserId[]; remove: UserId[] } => {
  const add = newUsers.filter((u) => !currentUsers.includes(u))
  const remove = currentUsers.filter((u) => !newUsers.includes(u))
  return { add, remove }
}

export const getParticipantCountString = (length: number, type: GameParticipantType): string => {
  return `${length} ${pluralize(length, type)}`
}

export const stringifyGameDates = (game: GameData): string => {
  const { startDate, endDate } = game
  if (game.gameType === 'bracket') {
    return toDisplayTimeline(startDate, endDate)
  }
  return `${formatDate(startDate)} -  ${formatDate(endDate)}`
}

export const stringifySeedingType = (seedingType: GameSeedingType | undefined): string => {
  if (isNotDefined(seedingType)) return 'Randomized'
  switch (seedingType) {
    case 'standard':
      return 'Seeding Round (Leaderboard)'
    case 'historical':
      return 'Use Historical Data'
  }
}

export const stringifyBracketRounds = (rounds: BracketRound[]): string => {
  const hasSeedingRound = rounds.some((r) => r.round === 0)

  if (hasSeedingRound) {
    const numOfBracketRounds = rounds.length - 1
    return `1 seeding round + ${stringifyRounds(numOfBracketRounds)}`
  }
  return stringifyRounds(rounds.length)
}

const stringifyRounds = (numOfRounds: number): string => {
  return `${numOfRounds} bracket ${pluralize(numOfRounds, 'round')}`
}

export const toReadableGameStatus = (status: GameStatus): string => {
  switch (status) {
    case 'draft':
      return 'Upcoming'
    case 'published':
      return 'Announced'
    case 'active':
      return 'Active'
    case 'finished':
      return 'Finished'
    default:
      return status
  }
}

export const startedRecently = (game: Pick<GameData, 'startDate' | 'status'>): boolean => {
  if (game.status !== 'active') return false
  const now = DateTime.now()
  // if the game has not started yet, it is not recent active
  if (now.toMillis() < game.startDate) return false
  // if the game has started more than 1 day ago, it is not recent active
  if (now.toMillis() - game.startDate >= DAY) return false
  return true
}

export const canHardDelete = (
  game: Pick<GameData, 'status' | 'startDate'>,
  participants: DecoratedGameParticipant[]
): boolean => {
  if (game.status !== 'draft') return false
  // avoid cache issue, where cached game is not updated when game is started
  // we can check if the game has automatically started if there are at least 2 participants
  const now = DateTime.now()
  if (game.startDate > now.toMillis()) {
    if (participants.length >= 2) return false
  }
  return true
}
